import React, { useState } from 'react';
import { Icons } from '../components/Icons';
import { db, appId } from '../utils/firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import AddDataModal from '../components/AddDataModal';
import DetailModal from '../components/DetailModal';

const JournalView = ({ data, isAdmin, showToast }) => {
    const [editing, setEditing] = useState(null);
    const [selected, setSelected] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [activeFilter, setActiveFilter] = useState('ALL');

    const journals = data?.journals || [];
    const sortedData = [...journals].sort((a, b) => (b.date || '').localeCompare(a.date || '')); 
    const filteredData = activeFilter === 'ALL' ? sortedData : sortedData.filter(j => (j.author === 'DR. MARSH' ? 'OPERATOR' : j.author) === activeFilter);

    const handleSave = async (formData) => {
        try {
            const coll = collection(db, 'artifacts', appId, 'public', 'data', 'journals');
            if (formData.id) await updateDoc(doc(coll, formData.id), formData);
            else await addDoc(coll, { ...formData, timestamp: serverTimestamp() });
            setEditing(null); setIsAdding(false);
            showToast("Log Saved", "success");
        } catch (err) { showToast("Firebase Error", "error"); }
    };

    const handleDelete = async (e, id) => { 
        e.stopPropagation();
        if (!window.confirm("Purge Log?")) return;
        try { await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'journals', id)); showToast("Log purged.", "success"); } 
        catch (e) { showToast("Database Error", "error"); } 
    };

    const getAuthorStyle = (author) => {
        // "Dr. Marsh" is the retired identity of the Operator role (the LAZARO entity's
        // current host, now Professor Stanton) -- old entries alias to OPERATOR, not CRI.
        const normalizedAuthor = author === 'DR. MARSH' ? 'OPERATOR' : author;

        switch(normalizedAuthor) {
            case 'BOB': return { color: 'text-yellow-500', border: 'border-yellow-500/50', icon: Icons.Activity };
            case 'CRI': return { color: 'text-green-500', border: 'border-green-500/50', icon: Icons.Database };
            case 'OPERATOR': return { color: 'text-cyan-400', border: 'border-cyan-500/50', icon: Icons.Eye };
            case 'TAG': return { color: 'text-red-500', border: 'border-red-500/50', icon: Icons.Palette };
            default: return { color: 'text-gray-400', border: 'border-gray-500/50', icon: Icons.BookOpen };
        }
    };

    return (
        <div className="flex-1 overflow-y-auto p-6 bg-[#050505] custom-scrollbar fade-in h-full pb-24">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6 border-b border-gray-900 pb-4 shrink-0">
                <h2 className="text-xl font-bold flex items-center gap-2 text-white"><Icons.BookOpen className="text-yellow-500" /> TEMPORAL LOGS</h2>
                <div className="flex items-center gap-4">
                    <div className="flex bg-black rounded border border-gray-800 p-1 overflow-x-auto custom-scrollbar">
                        {['ALL', 'BOB', 'CRI', 'OPERATOR', 'TAG', 'ESO'].map(f => (<button key={f} onClick={() => setActiveFilter(f)} className={`px-3 py-1 text-[9px] font-bold uppercase rounded transition-colors whitespace-nowrap ${activeFilter === f ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-white'}`}>{f}</button>))}
                    </div>
                    {isAdmin && <button onClick={() => setIsAdding(true)} className="bg-yellow-900/20 text-yellow-500 border border-yellow-500/50 hover:bg-yellow-900 px-3 py-1.5 rounded text-[10px] font-bold flex items-center gap-2 shrink-0"><Icons.Plus size={12} /> ADD LOG</button>}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 items-stretch">
                {filteredData.map(item => {
                    const style = getAuthorStyle(item.author);
                    const AuthorIcon = style.icon;
                    // "Dr. Marsh" entries display as OPERATOR -- same role, current host is Stanton
                    const displayAuthor = item.author === 'DR. MARSH' ? 'OPERATOR' : item.author;

                    return (
                        <div key={item.id} onClick={() => setSelected(item)} className={`bg-[#0a0a0a] border ${style.border} p-5 rounded shadow-lg hover:border-white transition-all cursor-pointer flex flex-col min-h-[16rem] h-full`}>
                            <div className="flex justify-between items-start border-b border-gray-800 pb-2 mb-4 shrink-0">
                                <div className="flex gap-2 items-center">
                                    <AuthorIcon size={14} className={style.color} />
                                    <span className={`text-[10px] font-bold tracking-widest uppercase ${style.color}`}>{displayAuthor}</span>
                                </div>
                                <div className="text-[9px] text-gray-500 font-mono">{item.date}</div>
                            </div>
                            <div className="flex-1 flex flex-col">
                                <h3 className="font-bold text-white text-lg leading-tight mb-2">{item.title}</h3>
                                {/* Strip raw HTML tags from the grid preview */}
                                <p className="text-sm text-gray-400 font-sans line-clamp-6 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: (item.text || '').replace(/<[^>]*>?/gm, '') }} />
                            </div>
                            <div className="flex justify-end items-center mt-4 pt-2 border-t border-gray-800 shrink-0">
                                {isAdmin && <button onClick={(e) => handleDelete(e, item.id)} className="text-gray-700 hover:text-red-500 transition-colors p-1 bg-black rounded"><Icons.Trash2 size={14}/></button>}
                            </div>
                        </div>
                    );
                })}
            </div>

            {selected && (
                <DetailModal 
                    item={selected} 
                    type="TEMPORAL LOG" 
                    isAdmin={isAdmin} 
                    onClose={() => setSelected(null)} 
                    onEdit={(item) => { 
                        setSelected(null); 
                        setTimeout(() => setEditing(item), 100); 
                    }} 
                />
            )}

            {(isAdding || editing) && (
                <AddDataModal 
                    type="journal_entry" 
                    initialData={editing} 
                    onClose={() => { setIsAdding(false); setEditing(null); }} 
                    onSubmit={handleSave} 
                    showToast={showToast} 
                    isAdmin={isAdmin} 
                />
            )}
        </div>
    );
};

export default JournalView;